import {
  CpuIcon,
  MoreHorizontalIcon,
  PencilIcon,
  PlugZapIcon,
  PlusIcon,
  TrashIcon,
} from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ToolMark } from '@/features/editor-access/components/ToolMark';
import { PANEL } from '@/features/workspace-overview/stateStyles';
import { SECTION_HEADING } from '@/shared/components/sectionHeadingClass';
import { EmptyState } from '@/shared/components/EmptyState';

import { harnessMarkClass, hasHarnessMark } from '../harnessColours';
import type { Harness } from '../types';
import { HarnessEngineChip } from './HarnessEngineChip';
import { HarnessTestConnectionButton } from './HarnessTestConnectionButton';
import { SetDefaultHarnessControl } from './SetDefaultHarnessControl';

const STATUS_BADGE: Record<
  Harness['connection_status'],
  {
    label: string;
    variant: 'secondary' | 'default' | 'destructive';
    className?: string;
  }
> = {
  untested: { label: 'Untested', variant: 'secondary' },
  // `board-state-done` is the brand's success token (a teal, not a raw green —
  // see docs/brand.md's "Success, running" row), given the same light-fill chip
  // treatment the workspace-usage thresholds already use rather than a colour
  // this feature would be inventing on its own.
  ok: {
    label: 'Connected',
    variant: 'secondary',
    className: 'bg-board-state-done/12 text-board-state-done',
  },
  failed: { label: 'Failed', variant: 'destructive' },
};

function HarnessRow({
  harness,
  onEdit,
  onDelete,
}: {
  harness: Harness;
  onEdit: (harness: Harness) => void;
  onDelete: (harness: Harness) => void;
}) {
  const status = STATUS_BADGE[harness.connection_status];

  return (
    // On a phone, a grid: the mark, the name and the menu on the first line,
    // the status and the test under the name. As one wrapping row the name was
    // clipped to "Capge" and the menu fell onto a line of its own (spec 184).
    <li className="border-border hover:bg-accent focus-within:bg-accent flex flex-wrap items-center gap-3 border-t px-4 py-2.5 transition-colors first:border-t-0 max-sm:grid max-sm:grid-cols-[auto_minmax(0,1fr)_auto]">
      {/* Claude Code and Codex get their own mark; any other type keeps the
          generic chip, and its `label` below still says which kind it is. */}
      <span
        aria-hidden="true"
        className={`flex size-10 shrink-0 items-center justify-center rounded-full ${harnessMarkClass(harness.harness_type)}`}
      >
        {hasHarnessMark(harness.harness_type) ? (
          <ToolMark tool={harness.harness_type} className="size-6" />
        ) : (
          <CpuIcon className="size-6" strokeWidth={2.5} />
        )}
      </span>

      {/* The name is its own block so `truncate` can end it in an ellipsis; on
          the flex row it clipped without one (spec 184). */}
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="flex min-w-0 items-center gap-2 text-base font-semibold">
          <span className="truncate" title={harness.name}>
            {harness.name}
          </span>
          <SetDefaultHarnessControl harness={harness} />
        </span>
        <HarnessEngineChip
          harnessType={harness.harness_type}
          label={harness.label}
        />
        {harness.base_url ? (
          <span className="text-muted-foreground truncate font-mono text-xs">
            {harness.base_url}
          </span>
        ) : null}
      </span>

      {/* `contents` on a wider screen, so these stay items of the row; a
          line of their own under the name on a phone. */}
      <span className="contents max-sm:col-span-2 max-sm:col-start-2 max-sm:row-start-2 max-sm:flex max-sm:flex-wrap max-sm:items-center max-sm:gap-3">
        <Badge variant={status.variant} className={status.className}>
          {status.label}
        </Badge>

        <HarnessTestConnectionButton id={harness.id} className="self-center" />
      </span>

      <DropdownMenu>
        <DropdownMenuTrigger
          render={
            <Button
              variant="ghost"
              size="icon"
              className="ml-auto shrink-0 max-sm:col-start-3 max-sm:row-start-1"
            >
              <MoreHorizontalIcon aria-hidden="true" />
              <span className="sr-only">Actions for {harness.name}</span>
            </Button>
          }
        />
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            onClick={() => {
              onEdit(harness);
            }}
          >
            <PencilIcon aria-hidden="true" />
            Edit
          </DropdownMenuItem>
          <DropdownMenuItem
            variant="destructive"
            onClick={() => {
              onDelete(harness);
            }}
          >
            <TrashIcon aria-hidden="true" />
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </li>
  );
}

/**
 * The organization's harnesses in one list, with one "Add harness" action
 * (spec 240 FR-001/FR-002). A harness of a type the deployment no longer lists
 * stays here and stays editable (FR-012).
 */
export function HarnessList({
  harnesses,
  onEdit,
  onDelete,
  onCreate,
  createDisabledReason,
}: {
  harnesses: Harness[];
  onEdit: (harness: Harness) => void;
  onDelete: (harness: Harness) => void;
  onCreate: () => void;
  /** Why Add is unavailable (no types to offer); `undefined` when it is available. */
  createDisabledReason?: string;
}) {
  return (
    <section
      className="flex flex-col gap-3"
      aria-labelledby="harnesses-heading"
    >
      <div className="flex items-center justify-between">
        <h3 id="harnesses-heading" className={SECTION_HEADING}>
          Harnesses
        </h3>
        <Button
          size="lg"
          disabled={createDisabledReason !== undefined}
          title={createDisabledReason}
          onClick={onCreate}
        >
          <PlusIcon aria-hidden="true" />
          Add harness
        </Button>
      </div>
      {createDisabledReason !== undefined ? (
        <p className="text-muted-foreground text-sm">{createDisabledReason}</p>
      ) : null}
      {harnesses.length === 0 ? (
        <EmptyState icon={PlugZapIcon} description="No harnesses yet." />
      ) : (
        <div className={PANEL}>
          <ul className="flex flex-col">
            {harnesses.map((harness) => (
              <HarnessRow
                key={harness.id}
                harness={harness}
                onEdit={onEdit}
                onDelete={onDelete}
              />
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}
